import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from '../patches/CustomOrbitControls';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as global from '../data/global';
import * as THREE from 'three';
import * as Components from '../components/@index.js';
import { MuiMaterials } from "../@index";
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import CableIcon from '@mui/icons-material/Cable';
import GrainLevelIcon from '@mui/icons-material/Battery50';
import Compass from '../assets/images/compass.svg';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useGesture } from '@use-gesture/react';
import axios from "axios";
import { CustomText3D } from "../3ds/_shared/CustomText3D";
import { useAuth } from "../provider/authProvider.js";
import { createApiEndpoint } from '../index.js';
import { useNavigate } from "react-router-dom";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Stice from '../3ds/bin/stice/Stice';
import { tryLayoutPreview3dCameraFit } from './previewSite3d.js';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { withScrollHintMenuProps } from './windowChrome/ScrollHintPaper.js';
import { catalogBinDataIdForBinRef } from './layoutTableProxy.js';
import { cableNumberFromCableRecord } from './siteLayoutStice.js';

const cableNumberLabel = (layoutCable, fallbackIndex) => {
    const n = cableNumberFromCableRecord({
        name: layoutCable?.cableName,
        cableName: layoutCable?.cableName,
    });
    if (n != null) return String(n);
    return layoutCable?.cableName || `Cable ${layoutCable?.cableId || layoutCable?.id || (fallbackIndex != null ? fallbackIndex + 1 : "")}`;
};

const selectedBinScoped = (selectedBinId) =>
    !!selectedBinId && selectedBinId !== "site-view" && selectedBinId !== "no-bins";

// Theme will be created inside component to be theme-aware

// Helper function to focus camera on a cable
const focusCameraOnCable = (cable) => {
    if (!global.gw.selectedSite.orbitControls || !cable) {
        return;
    }

    const controls = global.gw.selectedSite.orbitControls;
    
    // Calculate the bounding box of the cable
    const box = new THREE.Box3();
    cable.meshes.forEach(mesh => {
        if (mesh.visible) {
            box.expandByObject(mesh);
        }
    });

    if (box.isEmpty()) {
        return;
    }

    // Get the center and size of the cable
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    // Calculate appropriate camera distance based on cable size
    const maxDimension = Math.max(size.x, size.y, size.z);
    const cameraDistance = maxDimension * 1.5; // Increased zoom - reduced multiplier from 3 to 1.5

    // Calculate camera position
    const camera = controls.object;
    const cameraDirection = new THREE.Vector3();
    camera.getWorldDirection(cameraDirection);

    // Position camera at an angle to the cable
    const angle = Math.PI / 4; // 45 degrees
    const height = cameraDistance * Math.sin(angle);
    const horizontalDistance = cameraDistance * Math.cos(angle);

    // Calculate camera position relative to cable center
    const cameraPosition = new THREE.Vector3(
        center.x + horizontalDistance,
        center.y + height,
        center.z + horizontalDistance
    );

    // Set a flag to prevent camera reset
    global.gw.selectedSite.isCableFocused = true;
    
    // The original bin camera position is now saved when the bin is highlighted
    // This ensures we have the correct position for the current bin, not a previous bin's
    
    // Animate camera to new position
    controls.object.position.copy(cameraPosition);
    controls.updateCamera(center);
    
    // Save the camera position to prevent it from being reset
    global.gw.selectedSite.binCameraDistance = controls.object.position.distanceTo(center);
    
    // Scale text appropriately after a short delay to prevent interference
    setTimeout(() => {
        global.scaleBinText();
    }, 100);
};

const BinAndCableDropdown = ({ value, onChange, onExternalUpdate, children, onCableChange, onRef }) => {
    const [internalValue, setInternalValue] = useState(value);
    const [selectedCableId, setSelectedCableId] = useState("select-cable");
    const [cableVisibilityTrigger, setCableVisibilityTrigger] = useState(0);
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || 'dark');

    useEffect(() => {
        const onTheme = () => setCurrentTheme(global.gw.theme || 'dark');
        window.addEventListener('themeChanged', onTheme);
        return () => window.removeEventListener('themeChanged', onTheme);
    }, []);

    const isLight = currentTheme === 'light';
    const themeText = isLight ? '#333333' : '#f4cc8e';
    const themeMenuBg = isLight ? '#ffffff' : '#2d2d2d';
    const themeMenuHover = isLight ? '#f5f5f5' : '#3d3d3d';
    const selectMenuProps = {
        MenuProps: withScrollHintMenuProps({
            PaperProps: {
                className: "hide-scrollbar",
                sx: {
                    background: themeMenuBg,
                    '& .MuiList-root': {
                        background: themeMenuBg,
                    },
                    '& .MuiMenuItem-root': {
                        color: themeText,
                        '&:hover': { background: themeMenuHover },
                    },
                },
            },
        }),
    };
    const headerSelectSx = {
        minWidth: 128,
        maxWidth: 220,
        "& .MuiInputBase-root": {
            height: 36,
            fontSize: 13,
            fontWeight: 600,
        },
        "& .MuiSelect-select": {
            py: 0.75,
            px: 1.25,
        },
    };
    
    // Function to trigger cable visibility re-render
    const triggerCableVisibilityRerender = useCallback(() => {
        setCableVisibilityTrigger(prev => prev + 1);
    }, []);
    
    // Expose the trigger function globally
    useEffect(() => {
        if (global.gw.selectedSite) {
            global.gw.selectedSite.triggerCableVisibilityRerender = triggerCableVisibilityRerender;
        }
    }, [triggerCableVisibilityRerender]);
    
    useEffect(() => {
        setInternalValue(value);
    }, [value]);
    
    // Expose reset function to parent
    useEffect(() => {
        if (onRef) {
            onRef({
                resetCableSelection: () => setSelectedCableId("select-cable"),
                updateCableSelection: (cableId) => setSelectedCableId(cableId)
            });
        }
    }, [onRef]);
    
    // Also sync when the parent's onChange is called
    const handleChange = (e) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        onChange(e);
    };
    
    // Handle cable selection changes
    const handleCableChange = (e) => {
        const newCableId = e.target.value;
        setSelectedCableId(newCableId);
        
        // Call the parent callback if provided
        if (onCableChange) {
            onCableChange(newCableId);
        }
        
        // If selecting "Select Cable", reset to appropriate view
        if (newCableId === "select-cable") {
            // Clear any camera interference flags
            global.gw.selectedSite.zoom = false;
              
            // Check if we're currently focused on a cable
            if (global.gw.selectedSite.isCableFocused) {
                // Clear cable focus state first
                global.gw.selectedSite.isCableFocused = false;
                global.gw.selectedSite.focusedCableId = null;
                
                // Check if there's currently a bin selected in the bin dropdown
                const currentSelectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
                
                if (currentSelectedBin) {
                    // There's a bin selected - go to bin view (show only selected bin's cables)
                    // Show all cables in the selected bin
                    const selectedBinDataId = currentSelectedBin.binDataId;
                    global.restoreCableVisibility(selectedBinDataId);
                    
                    // Hide cables from other bins (this is bin view, not site view)
                    global.setUnselectedCablesOff();
                    
                    // Update the cable visibility state to reflect that all cables in this bin are now visible
                    global.gw.selectedSite.isCablesOff = false;
                    
                    // Update the cable toggle button visual state to show "on"
                    let tvcs = window.document.getElementsByClassName("cbi");
                    if (tvcs && tvcs.length > 0) {
                        const isLight = global.gw.theme === 'light';
                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                    }
                } else {
                    // No bin selected - go to site view (show all cables in the site)
                    global.siteView(); // This restores the site view camera position
                    
                    // Force setAllCablesOn to run by ensuring isCablesOff is true first
                    global.gw.selectedSite.isCablesOff = true;
                    global.setAllCablesOn();
                    
                    // Update the cable toggle button visual state to show "on"
                    let tvcs = window.document.getElementsByClassName("cbi");
                    if (tvcs && tvcs.length > 0) {
                        const isLight = global.gw.theme === 'light';
                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                    }
                }
            } else if (global.gw.selectedSite.isObjectSelected) {
                // We're in bin view (no cable focused) - go back to bin view (show all cables in this bin)
                const selectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
                if (selectedBin) {
                    // Selected bin binId
                    selectedBin.isSelected = false;
                    global.highlightStice(selectedBin.binId);

                    // Show all cables in the bin
                    // Restore visibility of all cables that were hidden by global.focusOnCable
                    if (selectedBin) {
                        const selectedBinDataId = selectedBin.binDataId;
                        global.restoreCableVisibility(selectedBinDataId);
                        
                        // Hide cables from other bins (this is bin view, not site view)
                        global.setUnselectedCablesOff();
                        
                        // Update the cable visibility state to reflect that all cables in this bin are now visible
                        // This ensures consistency with the cable toggle button state
                        global.gw.selectedSite.isCablesOff = false;
                        
                        // Update the cable toggle button visual state to show "on"
                        let tvcs = window.document.getElementsByClassName("cbi");
                        if (tvcs && tvcs.length > 0) {
                            const isLight = global.gw.theme === 'light';
                            tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                        }
                    }
                }
            }

            // No need to disable/enable orbit controls - this was causing issues
            // The controls should remain enabled for proper user interaction
            
            // Restore the original bin camera position
            if (global.gw.selectedSite.orbitControls && global.gw.selectedSite.originalBinCameraSaved) {
                const controls = global.gw.selectedSite.orbitControls;
                
                // Restore the original camera position for this bin
                if (global.gw.selectedSite.originalBinCameraPosition) {
                    controls.object.position.copy(global.gw.selectedSite.originalBinCameraPosition);
                }
                
                // Restore the original lookAt if available
                if (global.gw.selectedSite.originalBinCameraLookAt) {
                    controls.updateCamera(global.gw.selectedSite.originalBinCameraLookAt);
                }
                
                // Force a clean update
                controls.update();
            }
             
            // IMPORTANT: Clear the cable focus flag AFTER we've done all the highlighting
            // This prevents saveBinCamera from being called with the wrong position
            global.gw.selectedSite.isCableFocused = false;
            global.gw.selectedSite.focusedCableId = null;

            // No need to disable/enable orbit controls - this was causing issues
            // The controls should remain enabled for proper user interaction
            
            // Restore the original bin camera position
            if (global.gw.selectedSite.orbitControls && global.gw.selectedSite.originalBinCameraSaved) {
                const controls = global.gw.selectedSite.orbitControls;
                
                // Restore the original camera position for this bin
                if (global.gw.selectedSite.originalBinCameraPosition) {
                    controls.object.position.copy(global.gw.selectedSite.originalBinCameraPosition);
                }
                
                // Restore the original lookAt if available
                if (global.gw.selectedSite.originalBinCameraLookAt) {
                    controls.updateCamera(global.gw.selectedSite.originalBinCameraLookAt);
                }
                
                // Force a clean update
                controls.update();
            }
        } else if (newCableId && newCableId !== "select-cable") {
            // Find the cable in the global cablesRef using the selected cable ID
            // Simple cable selection - just like bin selection
            const selectedLayoutCable = global.gw.selectedSite.layoutCables?.find(layoutCable => 
                layoutCable.customerSiteLayoutCableId === parseInt(newCableId)
            );
            
            if (selectedLayoutCable) {
                // Remember where we came from BEFORE focusOnCable changes the bin selection
                // Check if any bin is currently selected
                const anyBinSelected = global.gw.selectedSite.binsRef.some(bin => bin.isSelected);
                global.gw.selectedSite.cameFromSiteView = !anyBinSelected;
                
                // Save the current camera position before focusing on the cable
                // This ensures we don't lose the site camera position
                if (!anyBinSelected) {
                    // We're in site view - save the site camera position ONLY if we haven't already saved it
                    // This prevents overwriting the original site camera position when selecting multiple cables
                    if (!global.gw.selectedSite.isCableFocused) {
                        global.saveSiteCamera();
                    }
                } else if (!global.gw.selectedSite.isCableFocused) {
                    // Save bin camera if we're coming from a bin view
                    global.saveBinCamera();
                }
                
                // Use the global function to focus on the cable
                // This will also select/highlight the bin if needed
                global.focusOnCable(selectedLayoutCable.cableId);
            }
        }
    };
    
    // Expose update function to parent - only run once when component mounts
    useEffect(() => {
        if (onExternalUpdate) {
    
            const updateFunction = (newValue) => {

                if (newValue !== null && newValue !== undefined) {
                    setInternalValue(newValue);
                }
            };
            
            onExternalUpdate(updateFunction);
        } else {

        }
    }, []); // Empty dependency array - only run once
    
    return (
        <>
        <MuiMaterials.TextField
            select
            size="small"
            value={internalValue}
            onChange={handleChange}
            label="Bin"
            variant="outlined"
            SelectProps={{
                ...selectMenuProps,
            }}
            sx={headerSelectSx}
        >
            {children}
        </MuiMaterials.TextField>
            <MuiMaterials.TextField
                select
                size="small"
                value={selectedCableId}
                onChange={handleCableChange}
            label="Cable"
            variant="outlined"
            SelectProps={{
                ...selectMenuProps,
                renderValue: (selectedValue) => {
                    // Custom render function to show cable names instead of IDs
                    if (selectedValue === "select-cable") {
                        return <em>Select Cable</em>;
                    }
                    
                    // Find the selected cable to get its name
                    const selectedLayoutCable = global.gw.selectedSite.layoutCables?.find(cable => 
                        cable.customerSiteLayoutCableId === parseInt(selectedValue)
                    );
                    
                    if (selectedLayoutCable) {
                        const binScoped = selectedBinScoped(internalValue);
                        const displayName = binScoped
                            ? cableNumberLabel(selectedLayoutCable)
                            : (selectedLayoutCable.cableName || cableNumberLabel(selectedLayoutCable));
                
                        return displayName;
                    }
                    
                    return selectedValue; // Fallback to showing the value
                }
            }}
            sx={headerSelectSx}
        >
                <MuiMaterials.MenuItem value="select-cable">
                    <em>Select Cable</em>
                </MuiMaterials.MenuItem>
                {(() => {
                    // Show all cables when no bin is selected, or filter by selected bin
                    const selectedBinId = internalValue;
                    
                    if (!selectedBinId || selectedBinId === "site-view" || selectedBinId === "no-bins") {
                        // Show all cables when no specific bin is selected (Site View mode)
                        const allCables = global.gw.selectedSite.layoutCables || [];

                        

                        
                        return allCables.map((cable, index) => {
                            const dropdownValue = cable.customerSiteLayoutCableId || cable.id || index.toString();
                            const displayName = cable.cableName || cableNumberLabel(cable, index);
                            
                            return (
                                <MuiMaterials.MenuItem 
                                    key={`cable-${dropdownValue}`} 
                                    value={dropdownValue}
                                    sx={{ 
                                        fontSize: "14px", 
                                        fontWeight: "700",
                                        color: themeText,
                                    }}
                                >
                                    {displayName}
                                </MuiMaterials.MenuItem>
                            );
                        });
                    }
                    
                    // Filter cables to only show those belonging to the selected bin
                    const binCables = global.gw.selectedSite.layoutCables?.filter(cable => 
                        cable.binId === parseInt(selectedBinId)
                    ) || [];
                    

                    
                    return binCables.map((cable, index) => {
                        const dropdownValue = cable.customerSiteLayoutCableId || cable.id || index.toString();
                        const displayName = cableNumberLabel(cable, index);
                        
                        return (
                            <MuiMaterials.MenuItem 
                                key={`cable-${dropdownValue}`} 
                                value={dropdownValue}
                                sx={{ 
                                    fontSize: "14px", 
                                    fontWeight: "700",
                                    color: themeText,
                                }}
                            >
                                {displayName}
                            </MuiMaterials.MenuItem>
                        );
                    });
                })()}
        </MuiMaterials.TextField>
        </>
    );
};

function SiteBinLevelToggle() {
    const [on, setOn] = useState(() => global.gw.selectedSite?.showSiteBinLevels !== false);
    const [themeTick, setThemeTick] = useState(0);

    useEffect(() => {
        const onTheme = () => setThemeTick((n) => n + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    const isLight = global.gw.theme === "light";
    const bright = isLight ? "#333333" : "#f4cc8e";
    const dimmed = isLight ? "#33333361" : "#f4cc8e61";
    const color = on ? bright : dimmed;

    return (
        <p style={{ margin: "0px" }}>
            <MuiMaterials.Tooltip title="Toggles Grain Level" arrow>
                <MuiMaterials.Button
                    key={`site3d-bin-level-${global.gw.theme}-${themeTick}`}
                    sx={{
                        background: "transparent",
                        color,
                        margin: "auto",
                        maxWidth: "30px",
                        maxHeight: "40px",
                        minWidth: "30px",
                        minHeight: "40px",
                        "&:hover": {
                            backgroundColor: isLight ? "#f5f5f5" : "#3d3d3d",
                        },
                        "& .MuiSvgIcon-root": { color },
                        "& .sbli": { color },
                    }}
                    onClick={() => {
                        const site = global.gw.selectedSite;
                        if (!site) return;
                        const next = site.showSiteBinLevels === false;
                        site.showSiteBinLevels = next;
                        setOn(next);
                        if (next) {
                            global.renderSiteBinLevels();
                        } else {
                            global.clearSiteBinLevels();
                        }
                    }}
                >
                    <GrainLevelIcon className="sbli" />
                </MuiMaterials.Button>
            </MuiMaterials.Tooltip>
        </p>
    );
}

function FaintSensorLabelEnforcer() {
    useFrame(() => global.enforceFaintSensorLabels());
    return null;
}

export const Site3d = React.memo(function Site3d({
    isHistoryLoading: propIsHistoryLoading,
    onOpenSaveModal,
    /** When true, close returns to Site Layout via `onLayoutPreviewClose` (in-editor preview). */
    layoutPreviewMode = false,
    onLayoutPreviewClose,
    /** When true, Site3dWindow owns WindowChrome (site | tools | close); bin/cable go in the header. */
    chromeEmbedded = false,
    headerActionsHost = null,
}) {
    const componentStartTime = performance.now();
    // console.log('[PERF] Site3d: Component rendering started');
    
    const navigate = useNavigate();
    const { setToken, token, clearToken } = useAuth();
    // Start with history loading as false since it's loaded in background and not required for initial render
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [binThermoLoading, setBinThermoLoading] = useState(false);
    const [selectedBinId, setSelectedBinId] = useState(["site-view"]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [displayLastScan, setDisplayLastScan] = useState(global.gw.selectedSite.lastScan || '');
    const [gradesVisibilityTrigger, setGradesVisibilityTrigger] = useState(0);
    const savedSiteViewAppliedRef = useRef(false);

    // Ensure selectedBinId is never null
    const safeSelectedBinId = selectedBinId && selectedBinId[0] ? selectedBinId[0] : "site-view";
    
    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);
    
    // Set grades re-render trigger - use separate state so it doesn't remount ThemeProvider/Canvas (which would reset camera)
    useEffect(() => {
        if (global.gw.selectedSite) {
            global.gw.selectedSite.triggerGradesRerender = () => setGradesVisibilityTrigger(prev => prev + 1);
        }
    }, []);

    useEffect(() => {
        if (isDataLoaded && global.gw.theme === 'light') {
            global.refreshAllSensorTemperatureColors?.();
        }
    }, [isDataLoaded, global.gw.theme]);

    // Drop stale Site Layout return flags (e.g. after a failed Preview Site navigation).
    useEffect(() => {
        const ret = global.peekSiteLayoutReturn?.();
        if (!ret) return;
        if (!ret.fromEditor) {
            global.clearSiteLayoutReturn?.();
            return;
        }
        if (!global.isSiteLayoutEditorReturn?.()) {
            global.clearSiteLayoutReturn?.();
        }
    }, []);

    useEffect(() => {
        if (layoutPreviewMode) return;
        global.resetSite3dToSiteOverview?.();
        setSelectedBinId(["site-view"]);
    }, [layoutPreviewMode]);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
            global.refreshAllSensorTemperatureColors?.();
            // Update icon colors when theme changes
            const isLight = global.gw.theme === 'light';
            const brightColor = isLight ? "#333333" : "#f4cc8e";
            const dimmedColor = isLight ? "#33333361" : "#f4cc8e61";
            const dimmedColorRgba = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            
            // Update all tvc icons
            const tvcs = window.document.getElementsByClassName("tvc");
            Array.from(tvcs).forEach(tvc => {
                const currentColor = tvc.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    tvc.style.color = dimmedColor;
                } else if (currentColor && (currentColor.includes("#333333") || currentColor.includes("rgb(51"))) {
                    tvc.style.color = isLight ? dimmedColor : "#f4cc8e61";
                }
            });
            
            // Update cbi icon
            const cbis = window.document.getElementsByClassName("cbi");
            Array.from(cbis).forEach(cbi => {
                const currentColor = cbi.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    cbi.style.color = brightColor;
                } else if (currentColor && (currentColor.includes("rgba(244"))) {
                    cbi.style.color = dimmedColorRgba;
                }
            });
            
            // Update sbli icon (bin grain level + product labels)
            const sblis = window.document.getElementsByClassName("sbli");
            Array.from(sblis).forEach(sbli => {
                const on = global.gw.selectedSite?.showSiteBinLevels !== false;
                sbli.style.color = on ? brightColor : dimmedColor;
            });
            
            // Update cmi icon
            const cmis = window.document.getElementsByClassName("cmi");
            Array.from(cmis).forEach(cmi => {
                const currentColor = cmi.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    cmi.style.color = brightColor;
                } else if (currentColor && (currentColor.includes("rgba(244"))) {
                    cmi.style.color = dimmedColorRgba;
                }
            });
            
            // Update cmp icon
            const cmps = window.document.getElementsByClassName("cmp");
            Array.from(cmps).forEach(cmp => {
                const currentColor = cmp.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    cmp.style.color = dimmedColor;
                } else if (currentColor && (currentColor.includes("#333333") || currentColor.includes("rgb(51"))) {
                    cmp.style.color = isLight ? dimmedColor : "#f4cc8e61";
                }
            });
            
            // Update site3dClose icon
            const site3dCloses = window.document.getElementsByClassName("site3dClose");
            Array.from(site3dCloses).forEach(close => {
                const currentColor = close.style.color;
                if (currentColor && (currentColor.includes("#f4cc8e") || currentColor.includes("rgb(244"))) {
                    close.style.color = brightColor;
                } else if (currentColor && (currentColor.includes("#333333") || currentColor.includes("rgb(51"))) {
                    close.style.color = isLight ? brightColor : "#f4cc8e";
                }
            });
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);
    
    // Create theme-aware theme object
    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#000000' : '#f4cc8e',
                            fontSize: "18px",
                            fontWeight: "700",
                            left: "-2px",
                            borderColor: isLight ? '#666666' : 'rgb(244, 204, 142)',
                            ...(isLight && {
                                '&:not(.MuiInputLabel-shrink)': {
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '14px',
                                },
                            }),
                        },
                        shrink: {
                            top: isLight ? '8px' : undefined,
                        },
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontSize: "15px",
                            background: "transparent",
                            color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)',
                        }
                    }
                },
                MuiIconButton: {
                    styleOverrides: {
                        sizeMedium: {
                            color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)'
                        }
                    }
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#333333' : '#f4cc8e',
                            "& .MuiOutlinedInput-notchedOutline": {
                                border: isLight ? "0.5px solid #cccccc" : "0.5px solid #f4cc8e4d",
                            },
                            "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: isLight ? "0.5px solid #666666" : "0.5px solid #f4cc8e",
                                },
                            },
                            "&:hover": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: isLight ? "0.5px solid #666666" : "0.5px solid #f4cc8e",
                                },
                            },
                        }
                    }
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            ".MuiInputLabel-root.Mui-focused": {
                                color: isLight ? '#000000' : '#f4cc8e',
                            },
                        },
                    }
                },
                MuiFormControl: {
                    styleOverrides: {
                        root: {
                            border: isLight ? "0.5px solid #cccccc" : "0.5px solid #f4cc8e4d",
                            ".MuiInputLabel-root.Mui-focused": {
                                color: isLight ? '#000000' : '#f4cc8e',
                            },
                            ".MuiSvgIcon-root": {
                                color: isLight ? '#333333' : '#f4cc8e',
                            },
                        },
                    }
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#333333' : '#f4cc8e',
                            background: isLight ? '#ffffff' : '#2d2d2d',
                            '&:hover': {
                                background: isLight ? '#f5f5f5' : '#3d3d3d',
                            },
                        },
                    }
                },
            },
        });
    }, [global.gw.theme]);
    
    // Sync prop with local state - but don't block UI if history is loading
    useEffect(() => {
        if (propIsHistoryLoading !== undefined) {
            setIsHistoryLoading(propIsHistoryLoading);
        }
    }, [propIsHistoryLoading]);
    
    // Removed problematic state tracking
    const updateDropdownRef = useRef(null);
    const dropdownRef = useRef(null);
    
    // Create a stable callback for the external update function
    const handleExternalUpdate = useCallback((updateFn) => {
        updateDropdownRef.current = updateFn;
    }, []);
    
    // Handle cable changes from the dropdown
    const handleCableChange = useCallback((cableId) => {
        // This callback is called when the cable selection changes in the dropdown
        // We can use this to perform any necessary actions without causing re-renders
        // Cable selection changed
    }, []);
    
    // Handle dropdown ref
    const handleDropdownRef = useCallback((ref) => {
        dropdownRef.current = ref;
    }, []);
    
    // Removed problematic useEffect
    
    // Function to get the current bin selection state
    const getCurrentBinSelection = () => {
        
        if (!global.gw.selectedSite.isObjectSelected) {
            return "site-view";
        }
        
        // Find the currently selected bin
        const selectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
        
        if (selectedBin) {
            const catalogId = catalogBinDataIdForBinRef(
                global.gw.selectedSite,
                selectedBin
            );
            if (catalogId != null && catalogId !== "") {
                return String(catalogId);
            }

            // Prefer binDataId directly if it is a catalog dropdown value
            if (selectedBin.binDataId != null && !Number.isNaN(selectedBin.binDataId)) {
                const bins = global.gw.selectedSite.bins || [];
                const matchesDropdown = bins.some(
                    (b) => String(b.binDataId) === String(selectedBin.binDataId)
                );
                if (matchesDropdown) {
                    return selectedBin.binDataId.toString();
                }
            }

            const bins = global.gw.selectedSite.bins || [];
            const resolved = bins.find(b => (
                (selectedBin.binName && b.binName === selectedBin.binName) ||
                (selectedBin.name && b.binName === selectedBin.name) ||
                (typeof selectedBin.customerSiteLayoutBinId === 'number' && b.customerSiteLayoutBinId === selectedBin.customerSiteLayoutBinId)
            ));
            
            if (resolved && resolved.binDataId != null && !Number.isNaN(resolved.binDataId)) {
                return resolved.binDataId.toString();
            }
            
            return "site-view";
        }
        
        return "site-view";
    };
    
    const siteSceneDataReady = useCallback(() => {
        const site = global.gw.selectedSite;
        if (!site) return false;
        if (layoutPreviewMode) {
            return (site.layoutBins?.length ?? 0) > 0;
        }
        return (
            (site.layoutCables?.length ?? 0) > 0 && !!site.isSiteInitialized
        );
    }, [layoutPreviewMode]);

    // Monitor when layout data becomes available (cables for production Site 3D, bins for layout preview)
    useEffect(() => {
        if (siteSceneDataReady()) {
            setIsDataLoaded(true);
        }
    }, [
        siteSceneDataReady,
        global.gw.selectedSite.layoutCables,
        global.gw.selectedSite.layoutBins,
        global.gw.selectedSite.isSiteInitialized,
        forceUpdate,
    ]);

    useEffect(() => {
        const onSiteReady = () => {
            if (siteSceneDataReady()) {
                setIsDataLoaded(true);
            }
        };
        window.addEventListener("grainwaveSelectedSiteReady", onSiteReady);
        return () =>
            window.removeEventListener("grainwaveSelectedSiteReady", onSiteReady);
    }, [siteSceneDataReady]);

    // siteView() in OrbitControls ref often runs before Stice populates groupsRef — retry when scene is ready.
    useEffect(() => {
        if (layoutPreviewMode || !isDataLoaded || savedSiteViewAppliedRef.current) {
            return undefined;
        }
        const site = global.gw.selectedSite;
        if (!site || site.zoom) {
            return undefined;
        }
        if (site.isCableFocused || site.isObjectSelected) {
            global.resetSite3dToSiteOverview?.();
            setSelectedBinId(["site-view"]);
        }

        const tryApplySavedSiteView = () => {
            const controls = site.orbitControls;
            const liveControls =
                controls
                && typeof controls.object?.getWorldDirection === "function"
                && typeof controls.updateCamera === "function";
            if (!liveControls || !(site.groupsRef?.length > 0) || !(site.siteRef?.length > 0)) {
                return false;
            }
            global.siteView();
            savedSiteViewAppliedRef.current = true;
            return true;
        };

        if (tryApplySavedSiteView()) return undefined;

        let attempts = 0;
        const timerId = window.setInterval(() => {
            attempts += 1;
            if (tryApplySavedSiteView() || attempts >= 25) {
                window.clearInterval(timerId);
            }
        }, 100);

        return () => window.clearInterval(timerId);
    }, [isDataLoaded, layoutPreviewMode, forceUpdate]);

    // Render flat-top BinLevel fills inside each bin from gw.selectedSite.binLevels.
    // Two trigger paths: (a) the binLevels fetch finishes after the 3D scene is up
    // (we listen to grainwave-bin-levels-updated), (b) the 3D scene finishes mounting
    // after the data already arrived (we kick off via the [isDataLoaded] effect below).
    //
    // Both paths defer through *two* animation frames once binsRef is populated:
    //   frame 1: lets Stice/BinStice ref callbacks finish wiring binRef.meshes
    //   frame 2: lets Stice's post-render useEffect apply bin3dModel.rotation for flat
    //            houses *before* renderSiteBinLevels samples wall geometry, so the
    //            fill picks up the rotation and stays centered.
    useEffect(() => {
        if (layoutPreviewMode) return undefined;

        let cancelled = false;
        const renderWhenReady = () => {
            if (cancelled) return;
            let attempts = 0;
            const tick = () => {
                if (cancelled) return;
                const binsRef = global.gw.selectedSite?.binsRef;
                if (Array.isArray(binsRef) && binsRef.length > 0 &&
                    binsRef.some(b => b?.parent?.parent?.parent)) {
                    requestAnimationFrame(() => {
                        if (cancelled) return;
                        global.renderSiteBinLevels();
                    });
                    return;
                }
                if (attempts++ < 30) {
                    requestAnimationFrame(tick);
                }
            };
            requestAnimationFrame(tick);
        };

        const handleBinLevelsUpdated = () => renderWhenReady();
        const handleBinLevelHistoryUpdated = () => renderWhenReady();
        window.addEventListener('grainwave-bin-levels-updated', handleBinLevelsUpdated);
        window.addEventListener('grainwave-bin-level-history-updated', handleBinLevelHistoryUpdated);

        // If data already arrived before this effect mounted, render now (idempotent --
        // renderSiteBinLevels clears prior meshes before drawing).
        if (Array.isArray(global.gw.selectedSite?.binLevels) && global.gw.selectedSite.binLevels.length > 0) {
            renderWhenReady();
        }

        return () => {
            cancelled = true;
            window.removeEventListener('grainwave-bin-levels-updated', handleBinLevelsUpdated);
            window.removeEventListener('grainwave-bin-level-history-updated', handleBinLevelHistoryUpdated);
            global.clearSiteBinLevels();
        };
    }, [layoutPreviewMode]);

    // When the 3D scene finishes loading (layoutCables + isSiteInitialized), retry the render
    // in case the binLevels fetch beat the scene to readiness (no event will fire after-the-fact).
    // Two-frame defer same as above so flat-house rotation has settled before sampling.
    useEffect(() => {
        if (layoutPreviewMode || !isDataLoaded) return;
        if (!Array.isArray(global.gw.selectedSite?.binLevels) || global.gw.selectedSite.binLevels.length === 0) return;
        requestAnimationFrame(() => requestAnimationFrame(() => global.renderSiteBinLevels()));
    }, [isDataLoaded, layoutPreviewMode]);

    // Simple function to update cable visibility button state - back to JavaScript approach
    const updateCableVisibilityButtonState = () => {
        const tvcs = window.document.getElementsByClassName("cbi");
        if (tvcs && tvcs.length > 0) {
            const areCablesVisible = !global.gw.selectedSite.isCablesOff;
            
            const isLight = global.gw.theme === 'light';
            const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
            const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
            if (areCablesVisible) {
                tvcs[0].style.color = brightColor;
            } else {
                tvcs[0].style.color = dimmedColor;
            }
        }
    };

    // Note: Button state is updated directly in onClick handlers and global functions

    // REMOVED: This useEffect was causing Site View reversion when selecting cables
    // It was triggering every time selectedBinId changed, interfering with cable selection
    
    // Set initial dropdown state based on current selection
    useEffect(() => {
        let testit = selectedBinId;

        const currentSelection = [getCurrentBinSelection()];
        setSelectedBinId(currentSelection);
        
        // Initialize cable visibility button state
        updateCableVisibilityButtonState();
        
        // Special handling for single-bin sites: if there's only one bin and no selection, default to it
        if (currentSelection === "site-view" && global.gw.selectedSite.bins && global.gw.selectedSite.bins.length === 1) {
            const singleBin = global.gw.selectedSite.bins[0];
            if (singleBin.binDataId) {
                setSelectedBinId(singleBin.binDataId.toString());
            }
        }
    }, [global.gw.selectedSite.bins]); // Run when bins data becomes available
    
    // Add a listener to update dropdown when bin selection changes
    useEffect(() => {
        // Set up the global update function - it will check the ref when called
        const updateDropdownOnBinClick = () => {
            const currentSelection = getCurrentBinSelection();
            if (currentSelection && currentSelection !== "site-view") {
                global.gw.selectedSite.selectedBinId = currentSelection;
            } else if (currentSelection === "site-view") {
                global.gw.selectedSite.selectedBinId = null;
            }
            
            // Update dropdown through ref without changing main state
            if (updateDropdownRef.current) {
                updateDropdownRef.current(currentSelection);
            }
            // Only reset cable selection when user selected a bin directly (not when selecting a cable
            // which also highlights its bin via focusOnCable)
            const fromCableSelection = global.gw.selectedSite?.highlightFromCableSelection;
            if (global.gw.selectedSite) {
                global.gw.selectedSite.highlightFromCableSelection = false;
            }
            if (!fromCableSelection && dropdownRef.current?.resetCableSelection) {
                dropdownRef.current.resetCableSelection();
                if (global.gw.selectedSite) {
                    global.gw.selectedSite.isCableFocused = false;
                    global.gw.selectedSite.focusedCableId = null;
                    const selectedBin = global.gw.selectedSite.binsRef?.find(b => b.isSelected);
                    if (selectedBin) {
                        global.restoreCableVisibility(selectedBin.binDataId ?? selectedBin.binId);
                        global.setUnselectedCablesOff();
                    }
                    if (global.gw.selectedSite.triggerCableVisibilityRerender) {
                        global.gw.selectedSite.triggerCableVisibilityRerender();
                    }
                }
            }
        };
        
        global.gw.selectedSite.updateBinDropdown = updateDropdownOnBinClick;
        
        return () => {
            global.gw.selectedSite.updateBinDropdown = null;
        };
    }, []); // No dependencies needed since we're using a ref
    
    // Add a listener to update cable dropdown when cable selection changes
    useEffect(() => {
        // Set up the global update function for cable dropdown
        const updateCableDropdownOnCableClick = (cableId) => {
            
            // Update cable dropdown through ref without changing main state
            if (dropdownRef.current && dropdownRef.current.updateCableSelection) {
                dropdownRef.current.updateCableSelection(cableId);
            }
        };
        
        global.gw.selectedSite.updateCableDropdown = updateCableDropdownOnCableClick;
        
        return () => {

            global.gw.selectedSite.updateCableDropdown = null;
        };
    }, []); // No dependencies needed since we're using a ref
    // Removed selectedCableId state to prevent React re-renders and flag resets
    // Cable selection is now handled by global functions only

    const renderBins = () => {
        // Safety check: ensure bins array exists and has data
        if (!global.gw.selectedSite.bins || global.gw.selectedSite.bins.length === 0) {
            return (
                <MuiMaterials.MenuItem value="no-bins" disabled>
                    <em>No bins available</em>
                </MuiMaterials.MenuItem>
            );
        }

        const sortedBins = [...global.gw.selectedSite.bins].sort((a, b) => {
            const an = Number(a?.number);
            const bn = Number(b?.number);
            if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
            return String(a?.binName || a?.name || "").localeCompare(
                String(b?.binName || b?.name || ""),
                undefined,
                { sensitivity: "base", numeric: true },
            );
        });

        return sortedBins.map((b, i) => (
            <MuiMaterials.MenuItem 
                key={`binSelect-${b.binDataId ?? b.binId ?? i}`} 
                value={b.binDataId != null ? String(b.binDataId) : String(b.binId ?? i)} 
                sx={{ 
                    fontSize: "14px", 
                    fontWeight: "700" 
                }}
            >
                {b.binName}
            </MuiMaterials.MenuItem>
        ));
    };

    const gesture = useGesture({
        onDragStart: e => {
            global.gw.selectedSite.mouseClickStartTime = Date.now();
            
            // Only clear cable focus if we're actually in pan mode (canDrag = true)
            // This prevents clearing cable focus when just rotating the camera in 3D Camera Mode
            if (global.gw.selectedSite.canDrag) {
                // If we were focused on a cable, restore visibility before clearing focus
                if (global.gw.selectedSite.isCableFocused && global.gw.selectedSite.focusedCableId) {
                    // Find the bin this cable belongs to and restore all cables in that bin
                    const layoutCable = global.gw.selectedSite.layoutCables?.find(lc => 
                        lc.cableId === parseInt(global.gw.selectedSite.focusedCableId)
                    );
                    
                    if (layoutCable) {
                        const binId = layoutCable.binId;
                        global.restoreCableVisibility(binId);
                    }
                }
                
                // Clear cable focus when user starts dragging in pan mode
                global.gw.selectedSite.isCableFocused = false;
                global.gw.selectedSite.focusedCableId = null;
                
                // Store initial screen coordinates for panning
                global.gw.selectedSite.mouseStartScreenX = e.values[0];
                global.gw.selectedSite.mouseStartScreenY = e.values[1];
            }
            // If canDrag is false (3D Camera Mode), don't clear cable focus - just store coordinates
            else {
                global.gw.selectedSite.mouseStartScreenX = e.values[0];
                global.gw.selectedSite.mouseStartScreenY = e.values[1];
            }
        },
        onDrag: e => {
            if (global.gw.selectedSite.canDrag) {
                // Get camera and its orientation
                const camera = global.gw.selectedSite.orbitControls?.object;
                const siteRoot = global.gw.selectedSite.siteRef?.[0];
                if (!camera || typeof camera.getWorldDirection !== "function" || !siteRoot) {
                    return;
                }
                const cameraDirection = new THREE.Vector3();
                camera.getWorldDirection(cameraDirection);
                
                // Create camera-relative vectors for panning
                const cameraRight = new THREE.Vector3();
                cameraRight.crossVectors(cameraDirection, camera.up).normalize();
                
                const cameraUp = new THREE.Vector3();
                cameraUp.crossVectors(cameraRight, cameraDirection).normalize();
                
                // Calculate mouse movement in screen space
                const mouseDeltaX = e.values[0] - global.gw.selectedSite.mouseStartScreenX;
                const mouseDeltaY = e.values[1] - global.gw.selectedSite.mouseStartScreenY;
                
                // Convert screen movement to world movement using camera orientation
                const panSpeed = 0.5; // Adjust this value to control pan sensitivity
                const worldDeltaX = mouseDeltaX * panSpeed;
                const worldDeltaY = -mouseDeltaY * panSpeed;
                
                // Apply movement in camera-relative directions
                const worldMovement = new THREE.Vector3();
                worldMovement.addScaledVector(cameraRight, worldDeltaX);
                worldMovement.addScaledVector(cameraUp, worldDeltaY);
                
                siteRoot.position.set(
                    global.gw.selectedSite.offset.x + worldMovement.x,
                    global.gw.selectedSite.offset.y + worldMovement.y,
                    global.gw.selectedSite.offset.z + worldMovement.z
                );
            }
        },
        onDragEnd: e => {
            if (global.gw.selectedSite.canDrag) {
                let s = global.gw.selectedSite.siteRef?.[0];
                if (!s) return;

                //global.setIsObjectSelected();
                if (!global.gw.selectedSite.isObjectSelected &&
                    e.elapsedTime > 400) {
                    if (global.gw.selectedSite.offset.x !== s.position.x ||
                        global.gw.selectedSite.offset.z !== s.position.z) {
                        global.gw.selectedSite.isOffsetChanged = true;
                    }
                }
                else {
                    //  Change camera lookat
                    //global.gw.selectedSite.orbitControls.currentLookAt.sub(s.position);
                    //global.gw.selectedSite.orbitControls.updateCamera(global.gw.selectedSite.orbitControls.currentLookAt);
                }

                global.gw.selectedSite.offset.x = s.position.x;
                global.gw.selectedSite.offset.y = s.position.y;
                global.gw.selectedSite.offset.z = s.position.z;
            }

            if (global.gw.selectedSite.isObjectSelected) {
                      // Only save bin camera if not focused on a cable
                      // This prevents overwriting the original bin camera position with cable zoom
                      if (!global.gw.selectedSite.isCableFocused) {
                global.saveBinCamera();
                      }
            }
            else {
                global.saveSiteCamera();
            }

            global.gw.selectedSite.mouseClickDownTime = Date.now() - global.gw.selectedSite.mouseClickStartTime;
        },
        onMouseDown: (state) => {
            if (state.event.button === 2) {
                let cameraPosition = 0;
                let cameraRotation = 0;
                let cameraAspect = 0;
                if (global.gw.selectedSite && global.gw.selectedSite.orbitControls && global.gw.selectedSite.orbitControls.object) {
                    cameraPosition = global.gw.selectedSite.orbitControls.object.position;
                    cameraAspect = global.gw.selectedSite.orbitControls.object.aspect;
                    cameraRotation = global.gw.selectedSite.orbitControls.object.rotation;
                }

                let rect = state.event.target.getBoundingClientRect();
                let p = new THREE.Vector3();
                p.set(
                    ((state.event.clientX - rect.left) / rect.width) * 2 - 1,
                    ((rect.top - state.event.clientY) / rect.height) * 2 + 1,
                    1
                );

                let wp = global.getWorldPointUnderCursor(state.event);
                let canvas = `canvas: ${p.x.toFixed(2)} ${p.y.toFixed(2)} ${p.z.toFixed(2)}`;
                let world = `world: ${wp.x.toFixed(2)} ${wp.y.toFixed(2)} ${-wp.z.toFixed(2)}`;
                let groups = "";

                if (global.gw.selectedSite && global.gw.selectedSite.groupsRef && global.gw.selectedSite.groupsRef.length > 0) {
                    p.set(
                        wp.x,
                        wp.y,
                        -wp.z
                    );
                    let v = new THREE.Vector3();
                    global.gw.selectedSite.groupsRef.forEach(g => {
                        v.copy(g.position);
                        let lv = v.sub(p);
                        groups += `group: ${Math.round(-lv.x, 2)} ${Math.round(lv.y, 2)} ${Math.round(lv.z, 2)}\n`;
                    });
                }

                let cLookAt = `cLookAt: ${global.gw.selectedSite.orbitControls.currentLookAt.x.toFixed(2)} ${global.gw.selectedSite.orbitControls.currentLookAt.y.toFixed(2)} ${global.gw.selectedSite.orbitControls.currentLookAt.z.toFixed(2)}`;
                let fov = `cFov: ${global.gw.selectedSite.orbitControls.object.fov}`;
                let zoom = `cZoom: ${global.gw.selectedSite.orbitControls.object.zoom}`;
                let distance = `cDistance: ${global.gw.selectedSite.orbitControls.object.position.distanceTo(global.gw.selectedSite.orbitControls.currentLookAt)}`;
                alert(`cPosition: ${Math.round(cameraPosition.x)} ${Math.round(cameraPosition.y)} ${Math.round(cameraPosition.z)} \ncRotation: ${Math.round(cameraRotation.x * 180 / Math.PI)} ${Math.round(cameraRotation.y * 180 / Math.PI)} ${Math.round(cameraRotation.z * 180 / Math.PI)} \n${cLookAt}\n${distance}\n${fov}\n${zoom}\naspect: ${cameraAspect} \n${canvas} \n${world} \n${groups}`);
            }
        },
        onPinchEnd: e => {
            // Only scale text and update camera distance if not focused on a cable
            if (!global.gw.selectedSite.isCableFocused) {
                global.scaleBinText();
                if (global.gw.selectedSite.isObjectSelected) {
                      // Save the current bin camera position
                      global.saveBinCamera();
                }
            }
            // When cable is focused, DON'T save the camera position
            // This prevents overwriting the original bin camera position
        },
        onWheelEnd: e => {
            // Only scale text and update camera distance if not focused on a cable
            if (!global.gw.selectedSite.isCableFocused) {
                global.scaleBinText();
                if (global.gw.selectedSite.isObjectSelected) {
                      // Save the current bin camera position
                      global.saveBinCamera();
                }
            }
              // When cable is focused, DON'T save the camera position
              // This prevents overwriting the original bin camera position
        },
    });

    useEffect(() => {
        if (global.gw.selectedSite.siteIndex < 0) return;
        //if (global.gw.siteInfos[global.gw.selectedSite.siteIndex].siteGuid !== "d25e8abf-e01a-4550-ab72-d24f2cf7f34e") return;

        //global.gw.selectedSite.refreshId = setTimeout(() => {
        //global.getBrxData();
        //}, global.gw.selectedSite.refreshRate);

        global.gw.selectedSite.refreshId = setTimeout(() => {
            global.getHeltecData();
        }, global.gw.selectedSite.refreshRate);

    }, []);
    
    // Subscribe to lastScan updates (polling / site init / hourly reload)
    useEffect(() => {
        const applyLastScan = (nextScan) => {
            if (global.gw.selectedSite.isCableFocused || global.gw.selectedSite.isObjectSelected) return;
            setDisplayLastScan(nextScan || global.gw.selectedSite.lastScan || '');
        };

        const handleLastScanUpdate = (event) => {
            applyLastScan(event?.detail?.lastScan);
        };

        global.gw.selectedSite.onLastScanUpdate = () => {
            applyLastScan(global.gw.selectedSite.lastScan);
        };

        window.addEventListener('lastScanUpdated', handleLastScanUpdate);
        return () => {
            global.clearCableNameHover?.();
            global.gw.selectedSite.onLastScanUpdate = null;
            window.removeEventListener('lastScanUpdated', handleLastScanUpdate);
        };
    }, []);

    // WebGL context restoration
    useEffect(() => {
        const handleContextLost = (event) => {
            console.warn('WebGL context lost, preventing default');
            event.preventDefault();
        };
        
        const handleContextRestored = () => {
            // Force re-render of the 3D scene
            window.location.reload();
        };
        
        const canvas = document.querySelector('canvas');
        if (canvas) {
            canvas.addEventListener('webglcontextlost', handleContextLost);
            canvas.addEventListener('webglcontextrestored', handleContextRestored);
        }
        
        return () => {
            if (canvas) {
                canvas.removeEventListener('webglcontextlost', handleContextLost);
                canvas.removeEventListener('webglcontextrestored', handleContextRestored);
            }
        };
    }, []);

    // Performance monitoring disabled to prevent requestAnimationFrame violations
    // useEffect(() => {
    //     let frameCount = 0;
    //     let lastTime = performance.now();
    //     let slowFrameCount = 0;
    //     let isThrottled = false;
    //     let isUserInteracting = false;
    //     let interactionTimeout = null;
    //     
    //     const monitorPerformance = () => {
    //         if (isThrottled || isUserInteracting) {
    //             // Skip monitoring during throttling or user interactions
    //             requestAnimationFrame(monitorPerformance);
    //             return;
    //         }
    //         
    //         frameCount++;
    //         const currentTime = performance.now();
    //         const deltaTime = currentTime - lastTime;
    //         
    //         if (deltaTime > 100) { // More than 10fps threshold (very lenient for 3D)
    //             slowFrameCount++;
    //             if (slowFrameCount > 100) { // Very high threshold to avoid false positives
    //                 console.warn(`Performance issue detected: ${slowFrameCount} slow frames in a row`);
    //                 // Throttle the render loop more aggressively
    //                 isThrottled = true;
    //                 setTimeout(() => {
    //                     slowFrameCount = 0;
    //                     isThrottled = false;
    //                 }, 15000); // Very long throttle period
    //             }
    //         } else {
    //             slowFrameCount = 0;
    //         }
    //         
    //         lastTime = currentTime;
    //         requestAnimationFrame(monitorPerformance);
    //     };
    //     
    //     // Monitor for user interactions
    //     const handleUserInteraction = () => {
    //         isUserInteracting = true;
    //         clearTimeout(interactionTimeout);
    //         interactionTimeout = setTimeout(() => {
    //             isUserInteracting = false;
    //         }, 1000); // Wait 1 second after interaction ends
    //     };
    //     
    //     // Listen for user interactions
    //     const canvas = document.querySelector('canvas');
    //     if (canvas) {
    //         canvas.addEventListener('mousedown', handleUserInteraction);
    //         canvas.addEventListener('mousemove', handleUserInteraction);
    //         canvas.addEventListener('wheel', handleUserInteraction);
    //         canvas.addEventListener('touchstart', handleUserInteraction);
    //         canvas.addEventListener('touchmove', handleUserInteraction);
    //     }
    //     
    //     // Start monitoring
    //     requestAnimationFrame(monitorPerformance);
    //     
    //     return () => {
    //         frameCount = 0;
    //         slowFrameCount = 0;
    //         isThrottled = false;
    //         isUserInteracting = false;
    //         clearTimeout(interactionTimeout);
    //         
    //         if (canvas) {
    //             canvas.removeEventListener('mousedown', handleUserInteraction);
    //             canvas.removeEventListener('mousemove', handleUserInteraction);
    //             canvas.removeEventListener('wheel', handleUserInteraction);
    //             canvas.removeEventListener('touchstart', handleUserInteraction);
    //             canvas.removeEventListener('touchmove', handleUserInteraction);
    //         }
    //     };
    // }, []);

    // Optimize click handlers
    const handleClickOptimized = useCallback((e) => {
        // Debounce rapid clicks
        if (e.target.dataset.clicked) return;
        e.target.dataset.clicked = 'true';
        setTimeout(() => {
            e.target.dataset.clicked = '';
        }, 100);
        
        // Your click handler logic here
     }, []);

    // Check if all required data is loaded
    // Note: isHistoryLoading is not required for initial render - history loads in background
    if (
        !isDataLoaded ||
        (!layoutPreviewMode && !global.gw.selectedSite.isSiteInitialized)
    ) {
        return (
            <MuiMaterials.Box
                key={`site3d-spinner-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    fontSize: "50px",
                    color: getThemeColor('rgb(51, 51, 51)', 'rgb(244, 204, 142)'),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: chromeEmbedded ? "absolute" : "fixed",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                    width: "100%",
                    height: "100%",
                    background: getThemeColor('rgba(255, 255, 255, 0.9)', 'rgba(0, 0, 0, 0.8)'),
                    zIndex: chromeEmbedded ? 2 : 9999,
                }}
            >
                <MuiMaterials.RotatingLines
                    strokeColor={getThemeColor('#333333', '#f4cc8e')}
                    strokeWidth="3"
                    animationDuration="0.72"
                    width="48"
                    visible={true}
                />
            </MuiMaterials.Box>
        );
    }

    return (
        <MuiMaterials.ThemeProvider theme={theme} key={`site3d-theme-${global.gw.theme}`}>
        <MuiMaterials.Box
            sx={
                chromeEmbedded
                    ? {
                        width: "100%",
                        height: "100%",
                        minHeight: 0,
                        position: "relative",
                        flex: 1,
                    }
                    : {
                        width: "100%",
                        height: "100%",
                        position: "relative",
                    }
            }
        >
            <Canvas
                key={`site3d_canvas`}
                camera={global.gw.selectedSite.defaultCamera}
                performance={{ min: 0.5 }}
                frameloop="always"
                dpr={[0.5, 1]}
                style={{
                    backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
                    height: "100%",
                    width: "100%",
                }}
                onPointerMove={(e) => {
                    global.updateBinLevelLabelHover(e.clientX, e.clientY);
                }}
                onPointerOut={() => {
                    global.clearBinLevelLabelHover();
                    global.clearCableNameHover();
                }}
                ref={el => {
                    if (!el) return;
                    const canvasReadyTime = performance.now();
                    // console.log('[PERF] Site3d: Canvas ref ready', (canvasReadyTime - componentStartTime).toFixed(2), 'ms after component start');
                    global.gw.selectedSite.canvasRef = el;
                    // Defer getBoundingClientRect to avoid blocking render
                    requestAnimationFrame(() => {
                        if (el) {
                            global.gw.selectedSite.canvasRect = el.getBoundingClientRect();
                            // console.log('[PERF] Site3d: Canvas fully initialized', (performance.now() - componentStartTime).toFixed(2), 'ms after component start');
                        }
                    });
                }}
                {...gesture()}
            >
                {global.gw && (!global.gw.geometries.An0 || !global.gw.geometries.AnPercent || !global.gw.geometries.AnDegree) ?
                    <group>
                        <CustomText3D 
                            scale={0.12}
                            side={THREE.DoubleSide}
                            position={new THREE.Vector3(0, 0, 0)}
                            meshProps={{
                                isHidden: true,
                                material:
                                    new THREE.MeshStandardMaterial({
                                        color: "#cccccc",
                                        transparent: true,
                                        opacity: 1,
                                        visible: false,
                                    })
                            }}
                        >
                            - 0123456789EO.%°FxABCDF
                        </CustomText3D>
                    </group>
                    :
                    null
                }

                <Components.GL site={global.gw.selectedSite}/>
                <ambientLight intensity={1.5}/>
                    {/*<directionalLight intensity={0.3} position={[5, 25, 20]} />*/}
                <directionalLight intensity={1.3} position={[0, 200, 0]} />
                
                    <Stice
                        site={global.gw.selectedSite}
                    />
                    {/* <Components.ArrowXYZ />
                    <Components.Grid3D {...global.gw.selectedSite.grid3D}/> */}
                
                <OrbitControls
                    key={`site3d_orbitControls`}
                    makeDefault
                    rotateSpeed={global.gw.selectedSite.orbitControls.rotateSpeed}
                    enablePan={false}
                    enableDamping={true}
                    dampingFactor={0.25}
                    enableRotate={global.gw.selectedSite.orbitControls.enableRotate}
                    ref={el => {
                        if (!el) {
                            // Unmount: don't leave a disposed controls instance on selectedSite.
                            if (
                                global.gw.selectedSite?.orbitControls
                                && typeof global.gw.selectedSite.orbitControls.enableRotate === "boolean"
                                && global.gw.selectedSite.orbitControls.object
                                && typeof global.gw.selectedSite.orbitControls.object.getWorldDirection === "function"
                            ) {
                                global.gw.selectedSite.canDrag = false;
                            }
                            return;
                        }
                        global.gw.selectedSite.orbitControls = el;

                        // Set camera limits
                        global.gw.selectedSite.orbitControls.minDistance = 0;
                        global.gw.selectedSite.orbitControls.maxDistance = Infinity;
                        global.gw.selectedSite.orbitControls.maxPolarAngle = Math.PI / 1.8;
                        global.gw.selectedSite.orbitControls.minPolarAngle = 0;
                        global.gw.selectedSite.orbitControls.maxAzimuthAngle = Infinity;
                        global.gw.selectedSite.orbitControls.minAzimuthAngle = -Infinity;

                        if (layoutPreviewMode) {
                            if (global.gw.selectedSite.layoutPreviewWantsCameraMode) {
                                global.set3dCameraModeOn();
                                delete global.gw.selectedSite
                                    .layoutPreviewWantsCameraMode;
                            }
                            requestAnimationFrame(() => {
                                tryLayoutPreview3dCameraFit();
                            });
                            return;
                        }

                        // In site view: load saved view (or default). In bin view: fitCamera runs from highlightStice.
                        if (
                            !global.gw.selectedSite.zoom &&
                            !global.gw.selectedSite.isCableFocused &&
                            !global.gw.selectedSite.isObjectSelected
                        ) {
                            global.siteView();
                        }
                    }}
                    name={"initialName"}
                    minPolarAngle={0}
                    maxPolarAngle={Math.PI / 1.8}
                />
            
                <FaintSensorLabelEnforcer />
                {global.gw.theme !== 'light' && (
                <EffectComposer disableNormalPass>
                    <Bloom luminanceThreshold={5} mipmapBlur />
                </EffectComposer>
                )}
                {/*<Environment preset="sunset" blur={0.9} />*/}
                </Canvas>
                
                {!layoutPreviewMode && headerActionsHost
                    ? createPortal(
                        <BinAndCableDropdown
                            value={
                                global.gw.selectedSite.selectedBinId
                                    ? String(global.gw.selectedSite.selectedBinId)
                                    : "site-view"
                            }
                            onCableChange={handleCableChange}
                            onChange={(e) => {
                                const selectedBinId = e.target.value;
                                
                                // PREVENT INTERFERENCE: If this onChange is triggered during cable selection,
                                // don't run the handler to avoid clearing cable focus
                                // BUT allow switching to different bins even when a cable is focused
                                // AND allow selecting the same bin again to deselect it
                                if (global.gw.selectedSite.isCableFocused && selectedBinId === global.gw.selectedSite.selectedBinId && global.gw.selectedSite.isObjectSelected) {
                                    return;
                                }
                                
                                // PREVENT INTERFERENCE: If we're in the middle of a cable selection process,
                                // prevent any bin dropdown changes to avoid interference
                                // BUT allow switching to different bins
                                // AND allow selecting the same bin again to deselect it
                                if (global.gw.selectedSite.focusedCableId && selectedBinId === global.gw.selectedSite.selectedBinId && global.gw.selectedSite.isObjectSelected) {
                                    return;
                                }
                                
                                // Handle case where no bins are available
                                if (selectedBinId === "no-bins") {
                                    return; // Do nothing for disabled option
                                }
                                       
                                if (selectedBinId === "site-view") {
                                    // Clear bin selection and show all cables
                                    global.gw.selectedSite.isObjectSelected = false;
                                    const currentSelectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
                                    if (currentSelectedBin) {
                                        // Manually deselect the current bin
                                        currentSelectedBin.isSelected = false;
                                        currentSelectedBin.meshes.forEach(y => {
                                            if (y.isWallMesh) {
                                                y.material.opacity = 0.08;
                                                y.material.emissive.setHex(global.gw.selectedSite.defaultEmissiveHex);
                                                y.material.envMapIntensity = 1;
                                                y.material.toneMapped = true;
                                            }
                                        });
                                    }
                                    
                                    // Restore site camera position
                                    global.siteView();
                                    
                                    // Show all cables when in Site View
                                    global.setAllCablesOn();
                                    
                                    // Reset cable selection to "Select Cable" when going to Site View
                                    if (dropdownRef.current && dropdownRef.current.resetCableSelection) {
                                        dropdownRef.current.resetCableSelection();
                                    }
                                    
                                    // Clear cable focus state
                                    global.gw.selectedSite.isCableFocused = false;
                                    global.gw.selectedSite.focusedCableId = null;
                                } else if (selectedBinId) {
                                    const binRef = global.findBinRefFromDropdownId(selectedBinId);
                                    
                                    if (binRef) {
                                        // If we're focused on a cable and switching to a different bin,
                                        // simulate "Select Cable" first, then select the new bin
                                        if (global.gw.selectedSite.isCableFocused) {
                                            const currentSelectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
                                            if (currentSelectedBin && currentSelectedBin.binDataId !== binRef.binDataId) {
                                                // We're switching to a different bin while focused on a cable
                                                // Simulate "Select Cable" action first - this restores the proper bin camera
                                                if (global.gw.selectedSite.isObjectSelected) {
                                                    // We're in bin view - return to bin view (show all cables in this bin)
                                                    // Simulating Select Cable for binId
                                                    currentSelectedBin.isSelected = false;
                                                    global.highlightStice(currentSelectedBin.binId);

                                                    // Show all cables in the bin
                                                    const selectedBinDataId = currentSelectedBin.binDataId;
                                                    global.restoreCableVisibility(selectedBinDataId);
                                                    global.setUnselectedCablesOff();
                                                    global.gw.selectedSite.isCablesOff = false;
                                                    
                                                    // Update cable toggle button visual state
                                                    let tvcs = window.document.getElementsByClassName("cbi");
                                                    if (tvcs && tvcs.length > 0) {
                                                        const isLight = global.gw.theme === 'light';
                                                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                                    }
                                                }
                                                
                                                // Clear cable focus state
                                                global.gw.selectedSite.isCableFocused = false;
                                                global.gw.selectedSite.focusedCableId = null;
                                            }
                                        }

                                        // Save the current site camera position before selecting the bin
                                        // This ensures we don't lose the site camera when switching between bins or selecting cables
                                        if (!global.gw.selectedSite.isObjectSelected) {
                                            global.saveSiteCamera();
                                        } else if (!global.gw.selectedSite.isCableFocused) {
                                            // Only save bin camera if we're actually changing to a different bin and a cable is not focused
                                            const currentSelectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
                                            if (currentSelectedBin && currentSelectedBin.binDataId !== binRef.binDataId) {
                                                global.saveBinCamera();
                                            }
                                        }
                                        
                                        // Set a flag to prevent saveBinCamera from being called during the transition
                                        // This prevents overwriting the saved bin camera with the site camera position
                                        global.gw.selectedSite.isTransitioningFromSiteView = !global.gw.selectedSite.isObjectSelected;
                                        // Setting transition flag
                                        
                                        // Clear the original bin camera flag so it gets saved for the new bin
                                        global.gw.selectedSite.originalBinCameraSaved = false;

                                        // Check if this bin is already selected - if so, treat it like "site-view"
                                        if (binRef.isSelected) {
                                            // Deselect the current bin and go to Site View
                                            global.highlightStice(binRef.binId);
                                            
                                            global.gw.selectedSite.selectedBinId = null;
                                            if (updateDropdownRef.current) {
                                                updateDropdownRef.current("site-view");
                                            }
                                            
                                            // Reset cable selection to "Select Cable" when going to Site View
                                            if (dropdownRef.current && dropdownRef.current.resetCableSelection) {
                                                dropdownRef.current.resetCableSelection();
                                            }
                                            
                                            // Clear cable focus state
                                            global.gw.selectedSite.isCableFocused = false;
                                            global.gw.selectedSite.focusedCableId = null;
                                            
                                            if (typeof window !== "undefined" && window.dispatchEvent) {
                                                window.dispatchEvent(new CustomEvent("grainwave-bin-levels-updated"));
                                            }
                                            return;
                                        }
                                        
                                        // Reset cable selection when bin changes
                                        if (dropdownRef.current && dropdownRef.current.resetCableSelection) {
                                            dropdownRef.current.resetCableSelection();
                                        }
                                        
                                        // Now select the new bin using the exact same function as clicking
                                        global.highlightStice(binRef.binId);
                                        
                                        // Clear the transition flag after the bin selection is complete
                                        global.gw.selectedSite.isTransitioningFromSiteView = false;
                                        // Cleared transition flag
                                        
                                        // Use standard bin camera behavior - highlightStice already handles the camera positioning
                                        // No need to override with saved camera angles when switching bins
                                        
                                        // Keep the catalog dropdown id (site.bins.binDataId), not
                                        // binsRef.binDataId which is layoutBin.binId on Temputech sites.
                                        global.gw.selectedSite.selectedBinId = selectedBinId;
                                        
                                        // Call the bin change handler to reset cable selection if it exists
                                        if (global.gw.selectedSite.onBinChange) {
                                            global.gw.selectedSite.onBinChange();
                                        }
                                        
                                        // Update the dropdown to reflect the new selection
                                        if (updateDropdownRef.current) {
                                            updateDropdownRef.current(String(selectedBinId));
                                        }
                                             
                                        // Ensure highlighting persists immediately (no delays needed)
                                        if (binRef.isSelected) {
                                            binRef.meshes.forEach(y => {
                                                if (y.isWallMesh) {
                                                    y.material.opacity = 1.0;
                                                    y.material.emissive.setHex(global.gw.selectedSite.selectedColorHex);
                                                    y.material.envMapIntensity = 10;
                                                    y.material.toneMapped = false;
                                                }
                                            });
                                            
                                        }
                                             
                                        // Check if we're switching to a different bin and restore saved camera
                                        // We need to check BEFORE the bin is selected, so we look for the currently selected bin
                                        const currentlySelectedBin = global.gw.selectedSite.binsRef.find(bin => bin.isSelected);
                                        if (currentlySelectedBin && currentlySelectedBin.binId !== binRef.binId) {
                                            // We're switching to a different bin, restore the saved camera
                                            if (global.gw.selectedSite.orbitControls && 
                                                global.gw.selectedSite.lastBinCameraPosition) {
                                                
                                                const controls = global.gw.selectedSite.orbitControls;
                                                
                                                // Restore the saved camera position (which includes the correct zoom/distance)
                                                controls.object.position.copy(global.gw.selectedSite.lastBinCameraPosition);
                                                
                                                // Restore lookAt if available
                                                if (global.gw.selectedSite.lastBinCameraLookAt) {
                                                    controls.updateCamera(global.gw.selectedSite.lastBinCameraLookAt);
                                                }
                                                
                                                // Restore the exact zoom level to maintain the same view
                                                if (global.gw.selectedSite.lastBinCameraZoom !== undefined) {
                                                    controls.object.zoom = global.gw.selectedSite.lastBinCameraZoom;
                                                }
                                                
                                                // Force a clean update
                                                controls.update();
                                            }
                                        }
                                        
                                        // Save the current bin's camera position as the original position
                                        if (global.gw.selectedSite.orbitControls) {
                                            global.gw.selectedSite.originalBinCameraPosition = global.gw.selectedSite.orbitControls.object.position.clone();
                                            global.gw.selectedSite.originalBinCameraLookAt = global.gw.selectedSite.orbitControls.currentLookAt.clone();
                                            global.gw.selectedSite.originalBinCameraSaved = true;
                                        }
                                    }
                                }
                                if (typeof window !== "undefined" && window.dispatchEvent) {
                                    window.dispatchEvent(new CustomEvent("grainwave-bin-levels-updated"));
                                }
                                global.scaleBinText();
                            }}
                            onExternalUpdate={handleExternalUpdate}
                            onRef={handleDropdownRef}
                        >
                            <MuiMaterials.MenuItem value="site-view">
                                <em>Site View</em>
                            </MuiMaterials.MenuItem>
                            {renderBins()}
                        </BinAndCableDropdown>,
                    headerActionsHost,
                )
                : null}
            {!layoutPreviewMode && (
                <>
                    <div
                        className="dateLabel"
                        style={{
                            position: "absolute",
                            bottom: "0px",
                            right: "0px",
                            margin: "auto",
                            textAlign: "center",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            userSelect: "none",
                            pointerEvents: "none",
                            zIndex: "10",
                        }}
                    >
                        <MuiMaterials.InputLabel
                            sx={{ color: getThemeColor('#000000', '#f4cc8e'), fontSize: "16px" }}
                        >
                            {displayLastScan}
                        </MuiMaterials.InputLabel>
                    </div>
                    <Components.DateSlider fromView="site3d" />
                    <Components.Comparison />
                </>
            )}
            <MuiMaterials.Box
                sx={{
                    position: "absolute",
                    bottom: 0,
                    top: chromeEmbedded ? 8 : 60,
                    right: 0,
                    margin: "auto",
                    textAlign: "center",
                    flexDirection: "column",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end",
                    width: "28px",
                    height: "280px",
                }}
            >
                {!layoutPreviewMode && (
                    <SiteBinLevelToggle />
                )}
                <p style={{ margin: "0px" }}>
                        <MuiMaterials.Tooltip title="3D Pan Mode" arrow>
                            <MuiMaterials.Button
                                key={`site3d-pan-${global.gw.theme}-${forceUpdate}`}
                                sx={{
                                    background: "transparent",
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                    margin: "auto",
                                    maxWidth: "30px",
                                    maxHeight: "40px",
                                    minWidth: "30px",
                                    minHeight: "40px",
                                    '&:hover': {
                                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: getThemeColor('#33333361', '#f4cc8e61'),
                                    },
                                    '& .tvc': {
                                        color: getThemeColor('#33333361', '#f4cc8e61'),
                                    },
                                }}
                                onClick={e => {
                                    if (global.gw.selectedSite && global.gw.selectedSite.orbitControls) {
                                        global.gw.selectedSite.canDrag = true;
                                        global.gw.selectedSite.orbitControls.enableRotate = false;
                                    }
                                    const isLight = global.gw.theme === 'light';
                                    const brightColor = isLight ? "#333333" : "#f4cc8e";
                                    const dimmedColor = isLight ? "#33333361" : "#f4cc8e61";
                                    let tvcs = window.document.getElementsByClassName("tvc");
                                    Array.from(tvcs).forEach(tvc => {
                                        tvc.style.color = dimmedColor;
                                    })
                                    e.currentTarget.children[0].style.color = brightColor;
                                }}
                            >
                                <ControlCameraIcon className="tvc" />
                            </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="3D Camera Mode" arrow>
                        <MuiMaterials.Button
                            key={`site3d-camera-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                                '& .tvc': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onClick={e => {
                                if (global.gw.selectedSite && global.gw.selectedSite.orbitControls) {
                                    global.gw.selectedSite.canDrag = false;
                                    global.gw.selectedSite.orbitControls.enableRotate = true;
                                }
                                const isLight = global.gw.theme === 'light';
                                const brightColor = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                const dimmedColor = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                let tvcs = window.document.getElementsByClassName("tvc");
                                Array.from(tvcs).forEach(tvc => {
                                    tvc.style.color = dimmedColor;
                                })
                                e.currentTarget.children[0].style.color = brightColor;
                            }}
                        >
                            <ThreeDRotationIcon className="tvc" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                {!layoutPreviewMode && (
                <>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Save View" arrow>
                        <MuiMaterials.Button
                            key={`site3d-saveview-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (global.isAdminUser(global.gw.email)) {
                                    onOpenSaveModal?.();
                                    return;
                                }
                                global.resetSite3dToSiteOverview?.();
                                setSelectedBinId(["site-view"]);
                                const payload = global.buildSiteViewPayload();
                                if (!payload) return;
                                await createApiEndpoint().saveUserCustomerSiteSavedView(
                                    "direct",
                                    null,
                                    "Site",
                                    0,
                                    payload
                                );
                            }}
                        >
                            <SaveIcon />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Toggle Cable Visibility" arrow>
                        <MuiMaterials.Button
                            key={`site3d-cable-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                                '& .cbi': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onClick={e => {
                                // Check if we're in a cable-focused state
                                if (global.gw.selectedSite.isCableFocused && global.gw.selectedSite.focusedCableId) {
                                    // We're focused on a cable - toggle to show all cables visible
                                    const selectedCable = global.gw.selectedSite.cablesRef.find(cable => 
                                        cable.cableId === parseInt(global.gw.selectedSite.focusedCableId)
                                    );
                                    
                                    if (selectedCable) {
                                        // Find the bin this cable belongs to
                                        const layoutCable = global.gw.selectedSite.layoutCables?.find(lc => 
                                            lc.cableId === parseInt(global.gw.selectedSite.focusedCableId)
                                        );
                                        
                                        if (layoutCable) {
                                            const binId = layoutCable.binId;
                                            
                                            // Use the global method to restore cable visibility
                                            global.restoreCableVisibility(binId);
                                            
                                            // Store the cable ID for potential re-focusing later
                                            global.gw.selectedSite.lastFocusedCableId = global.gw.selectedSite.focusedCableId;
                                            
                                            // Clear cable focus state but keep track that we can toggle back
                                            global.gw.selectedSite.isCableFocused = false;
                                            global.gw.selectedSite.focusedCableId = null;
                                            global.gw.selectedSite.canToggleBackToCable = true;
                                            
                                            // Update button visual state to show "on" (all cables are now visible)
                                            let tvcs = window.document.getElementsByClassName("cbi");
                                            if (tvcs && tvcs.length > 0) {
                                                const isLight = global.gw.theme === 'light';
                                                tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                            }
                                        }
                                    }
                                } else if (global.gw.selectedSite.canToggleBackToCable && global.gw.selectedSite.lastFocusedCableId) {
                                    // We can toggle back to cable focus - restore cable focus
                                    global.focusOnCable(global.gw.selectedSite.lastFocusedCableId);
                                    
                                    // Clear the toggle back flag
                                    global.gw.selectedSite.canToggleBackToCable = false;
                                    global.gw.selectedSite.lastFocusedCableId = null;
                                    
                                    // Update button visual state to show "off" (only one cable is visible)
                                    // Note: focusOnCable also sets this, but we set it here to be explicit
                                    let tvcs = window.document.getElementsByClassName("cbi");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                    }
                                } else {
                                    // Normal cable visibility toggle (Site View or Bin View)
                                    // In Site View: "ON" = all cables visible, "OFF" = no cables visible
                                    // In Bin View: "ON" = all cables visible, "OFF" = only selected bin cables visible
                                if (global.gw.selectedSite.isCablesOff) {
                                        // Currently OFF - turn ON (show all cables)
                                        global.setAllCablesOn();
                                        
                                        // Update button visual state to show "ON" (bright)
                                        let tvcs = window.document.getElementsByClassName("cbi");
                                        if (tvcs && tvcs.length > 0) {
                                            const isLight = global.gw.theme === 'light';
                                            tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                        }
                                    } else {
                                        // Currently ON - turn OFF (hide cables)
                                        global.setUnselectedCablesOff();
                                        
                                        // Update button visual state to show "OFF" (dimmed)
                                        let tvcs = window.document.getElementsByClassName("cbi");
                                        if (tvcs && tvcs.length > 0) {
                                            const isLight = global.gw.theme === 'light';
                                            tvcs[0].style.color = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                        }
                                    }
                                }
                            }}
                        >
                            <CableIcon className="cbi" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Toggle Invalid Cable Visibility" arrow>
                        <MuiMaterials.Button
                            key={`site3d-cable-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                padding: "0px",
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                                '& .cbi': {
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                },
                            }}
                            onClick={e => {
                                if (!global.gw.selectedSite.isInvalidCablesOn) {
                                    // Currently OFF - turn ON (show all invalid cables)
                                    global.setInvalidCables(true);
                                    
                                    // Update button visual state to show "ON" (bright)
                                    let tvcs = window.document.getElementsByClassName("civc");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                    }

                                    global.gw.selectedSite.isInvalidCablesOn = true;
                                } else {
                                    // Currently ON - turn OFF (hide invalid cables)
                                    global.setInvalidCables(false);
                                    
                                    // Update button visual state to show "OFF" (dimmed)
                                    let tvcs = window.document.getElementsByClassName("civc");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                    }

                                    global.gw.selectedSite.isInvalidCablesOn = false;
                                }
                            }}
                        >
                            <p className="civc">-E-</p>
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Toggle Grade Visibility" arrow>
                        <MuiMaterials.Button
                            key={`site3d-grades-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                padding: "0px",
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: global.gw.selectedSite?.isGradesOn !== false ? getThemeColor('#333333', '#f4cc8e') : getThemeColor('rgba(51, 51, 51, 0.38)', 'rgba(244, 204, 142, 0.38)'),
                                },
                                '& .cgi': {
                                    color: global.gw.selectedSite?.isGradesOn !== false ? getThemeColor('#333333', '#f4cc8e') : getThemeColor('rgba(51, 51, 51, 0.38)', 'rgba(244, 204, 142, 0.38)'),
                                },
                            }}
                            onClick={e => {
                                if (!global.gw.selectedSite.isGradesOn) {
                                    global.setGradesVisibility(true);
                                    let tvcs = window.document.getElementsByClassName("cgi");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                    }
                                } else {
                                    global.setGradesVisibility(false);
                                    let tvcs = window.document.getElementsByClassName("cgi");
                                    if (tvcs && tvcs.length > 0) {
                                        const isLight = global.gw.theme === 'light';
                                        tvcs[0].style.color = isLight ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                    }
                                }
                            }}
                        >
                            <p className="cgi">A+</p>
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="History Slider" arrow>
                        <MuiMaterials.Button
                            key={`site3d-history-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                border: "none",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                                '& .cmi': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={e => {
                                global.toggleHistorySlider();
                            }}
                        >
                            <CalendarMonthIcon className="cmi" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                <p style={{ margin: "0px" }}>
                    <MuiMaterials.Tooltip title="Comparison" arrow>
                        <MuiMaterials.Button
                            key={`site3d-comparison-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                background: "transparent",
                                color: getThemeColor('#33333361', '#f4cc8e61'),
                                margin: "auto",
                                maxWidth: "30px",
                                maxHeight: "40px",
                                minWidth: "30px",
                                minHeight: "40px",
                                '&:hover': {
                                    backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                                },
                                '& .MuiSvgIcon-root': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                                '& .cmp': {
                                    color: getThemeColor('#33333361', '#f4cc8e61'),
                                },
                            }}
                            onClick={e => {
                                global.toggleComparison();
                            }}
                        >
                            <CompareArrowsIcon className="cmp" />
                        </MuiMaterials.Button>
                    </MuiMaterials.Tooltip>
                </p>
                </>
                )}
            </MuiMaterials.Box>
            <img
                id={"compass"}
                key={`compass-${global.gw.theme}-${forceUpdate}`}
                style={{
                    position: "absolute",
                    top: chromeEmbedded ? "8px" : "44px",
                    right: "0px",
                    width: "45px",
                    height: "45px",
                    userSelect: "none",
                    pointerEvents: "none",
                    zIndex: "10",
                    // In light mode, filter to convert orangish colors to black
                    filter: global.gw.theme === 'light' 
                        ? 'brightness(0) saturate(100%)' // Converts all colors to black
                        : 'none',
                }}
                src={Compass}
                ref={el => {
                    if (!el) return;
                    global.gw.selectedSite.compass = window.document.getElementById("compass");
                }}
            />
            {global.isAdminUser(global.gw.email) &&
                global.isSiteLayoutEditorReturn?.() &&
                !layoutPreviewMode && (
                <MuiMaterials.Tooltip title="Back to site layout" arrow>
                    <MuiMaterials.Button
                        key={`site3d-layout-back-${global.gw.theme}-${forceUpdate}`}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            position: "absolute",
                            top: "4px",
                            left: "4px",
                            zIndex: 11,
                            textTransform: "none",
                            color: getThemeColor('#333333', '#f4cc8e'),
                            backgroundColor: getThemeColor('rgba(255,255,255,0.85)', 'rgba(45,45,45,0.9)'),
                            '&:hover': {
                                backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                            },
                        }}
                        onClick={() => {
                            clearTimeout(global.gw.selectedSite.refreshId);
                            global.navigateBackToSiteLayout(navigate);
                        }}
                    >
                        Site Layout
                    </MuiMaterials.Button>
                </MuiMaterials.Tooltip>
            )}
            {!chromeEmbedded ? (
            <p style={{ margin: "0px" }}>
                <MuiMaterials.Tooltip
                    title={
                        layoutPreviewMode ||
                        (global.isAdminUser(global.gw.email) &&
                            global.isSiteLayoutEditorReturn?.())
                            ? "Back to site layout"
                            : "Close"
                    }
                >
                    <MuiMaterials.Button
                        key={`site3d-close-${global.gw.theme}-${forceUpdate}`}
                        sx={{
                            background: "transparent",
                            color: getThemeColor('#333333', '#f4cc8e'),
                            position: "absolute",
                            top: "0px",
                            right: "0",
                            margin: "auto",
                            width: "45px",
                            height: "45px",
                            marginRight: "-7px",
                            zIndex: "10",
                            cursor: "pointer",
                            '&:hover': {
                                backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                            },
                            '& .MuiSvgIcon-root': {
                                color: getThemeColor('#333333', '#f4cc8e'),
                            },
                            '& .site3dClose': {
                                color: getThemeColor('#333333', '#f4cc8e'),
                            },
                        }}
                        onClick={e => {
                            clearTimeout(global.gw.selectedSite.refreshId);

                            if (layoutPreviewMode) {
                                onLayoutPreviewClose?.();
                                return;
                            }

                            global.clearSite3dPinned?.();

                            if (global.isAdminUser(global.gw.email) && global.isSiteLayoutEditorReturn?.()) {
                                if (global.navigateBackToSiteLayout(navigate)) {
                                    return;
                                }
                            } else {
                                global.clearSiteLayoutReturn?.();
                            }

                            let es = window.document.getElementsByName("Site 3D");
                            if (es && es.length > 0) {
                                es[0].style.display = "none";
                            }
                            navigate("/sites", { replace: true });
                        }}
                    >
                        <CloseIcon className="site3dClose" />
                    </MuiMaterials.Button>
                </MuiMaterials.Tooltip>
            </p>
            ) : null}
        </MuiMaterials.Box>
        </MuiMaterials.ThemeProvider>
    );
});

export default Site3d;


//<Environment preset="dawn" blur={0.9} />